/****************************************************************************************************
 * COMPONENT: LAYOUT NODES
 * --------------------------------------------------------------------------------------------------
 * Layout nodes are used to define the layout modifications for sections or blocks.
 * 
 * Layout nodes are defined in the CMS and are added to sections or blocks. These layout nodes are
 * used to define modifications that can be applied on a per section or per block basis. This allows
 * for fine grained control over the layout of the content.
 * 
 * In the CMS layout nodes are added as an array of layout nodes. This allows for more flexibitliy
 * and reuse of layout nodes. However, then means that the layout nodes need to be merged into a
 * single object that can be applied to the section or block. And that the layout nodes need to be
 * prioritized so that the last layout node in the array takes precedence.
 * 
 * This component will merge the layout nodes into a single object that can be used to apply the 
 * layout to the section or block. It will prioritize the last layout node in the array so that 
 * the last layout node takes precedence.
 * *************************************************************************************************/

import React from 'react';
import { Box } from '@mui/material';
import { SanityLayoutNode } from 'src/types/sanity';

interface LayoutNodesProps {
  layoutNodes: SanityLayoutNode[];
  children: React.ReactNode;
}

const LayoutNodes = ({ layoutNodes, children }: LayoutNodesProps) => {
  // * EXIT EARLY IF NO LAYOUT NODES OR CHILDREN
  if (!children) return null;

  /********************************************************************************
   * * CLEAN OBJECT
   * ------------------------------------------------------------------------------
   * This function will clean up an object by removing empty arrays. This is useful
   * when you want to remove empty arrays from an object. In this case, we are using
   * it to remove keys with empty arrays in order to simplify merging layout nodes.
   * ******************************************************************************/

  interface DirtyObject {
    [key: string]: string | string[];
  }

  const cleanObject = (dirtyObject: DirtyObject) => {
    const cleaned = { ...dirtyObject };
    Object.keys(cleaned).forEach(key => {
      if (Array.isArray(cleaned[key])) {
        // * DELETE EMPTY ARRAYS
        if (!cleaned[key].length) {
          delete cleaned[key];
        }
      }
    });

    return cleaned;
  };

  /********************************************************************************
   * * MERGE LAYOUT NODES
   * ------------------------------------------------------------------------------
   * Layout nodes are used to define the layout modifications for sections or blocks.
   * This function will merge the layout nodes into a single object that can be used
   * to apply the layout to the section or block.
   *
   * It will prioritize the last layout node in the array. This means that if there
   * are multiple layout nodes, the last one will take precedence.
   * ******************************************************************************/

  const getLayout = (layoutNodes: SanityLayoutNode[]) => {
    if (!layoutNodes || !layoutNodes.length) return null;

    const mergedLayout = layoutNodes.reduce((acc: SanityLayoutNode, node) => {
      // * CLEAN UP EMPTY ARRAYS
      const cleanPadding = cleanObject(node.padding);
      const cleanMargin = cleanObject(node.margin);


      return {
        ...acc,
        padding: { ...acc.padding, ...cleanPadding },
        margin: { ...acc.margin, ...cleanMargin },
      };
    });

    return mergedLayout;
  };

  const layout = getLayout(layoutNodes);
  
  return (
    <>
      <Box      
        // * LAYOUT NODES CONTAINER
        className="elo-section-layout-nodes"
                
        /**********************************************************************
         * * FLEX CONTAINER
         * --------------------------------------------------------------------
         * Layout nodes is a flex container because we need to be able to align
         * items within the layout node in the vertical and horizontal axis.
         * ********************************************************************/
        display={'flex'}
        flexDirection={'column'}
        // * Vertical Alignment
        justifyContent={layout?.justifyContent ?? undefined}


        {...layout?.padding}
        {...layout?.margin}        
        sx={{
          height: layout?.height?.height ?? undefined,
          minHeight: layout?.height?.minHeight ?? undefined,
          maxHeight: layout?.height?.maxHeight ?? undefined,
        }}
              
      >
        {children}
      </Box>
    </>
  );
};

export default LayoutNodes;
